import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type {
  CreateResult,
  DeleteResult,
  GetResult,
  ListResult,
  PaginatedResult,
  PaginationParams,
  ServiceContext,
  UpdateResult,
} from "@prolarus/shared";
import {
  toQueryString,
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryPaginated,
  tryUpdate,
  tryUpdatePost,
} from "./_shared";

export type Invoice = Tables<"invoices">;
export type InvoiceInsert = Omit<TablesInsert<"invoices">, "company_id">;
export type InvoiceUpdate = TablesUpdate<"invoices">;

export interface StatusChangeOptions {
  comments?: string;
  dueToReassignment?: boolean;
}

type InvoiceWithRelations = Invoice & {
  vendor?: { name: string } | null;
  status?: { name: string } | null;
};

type InvoiceWithVendor = Invoice & {
  vendor: { id: string; name: string; [k: string]: unknown } | null;
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
};

type InvoiceForEdit = Invoice & {
  vendor: { id: string; name: string } | null;
  current_status: { id: string; name: string; readonly: boolean } | null;
  price_book: { id: string; name: string } | null;
};

type InvoiceListWithRelations = Invoice & {
  vendor: { name: string } | null;
  current_status: { id: string; name: string; readonly: boolean } | null;
  external_system: string | null;
};

type InvoicePaginatedRow = InvoiceListWithRelations & {
  compliance_checks: { has_mismatch: boolean }[];
};

type InvoiceByParent = Invoice & {
  current_status: { id: string; name: string } | null;
};

type EarlyPayRow = {
  id: string;
  invoice_number: string | null;
  total_amount: number | null;
  discount: number | null;
  discount_amt: number | null;
  discount_expiration: string | null;
  due_date: string | null;
  status_id: string | null;
  vendor: { name: string } | null;
  current_status: { id: string; name: string } | null;
  compliance_checks: { has_mismatch: boolean }[] | null;
};

export const invoiceService = {
  get(id: string): Promise<GetResult<Invoice>> {
    return tryGet<Invoice>(`/api/invoices/${encodeURIComponent(id)}`);
  },

  getWithRelations(id: string): Promise<GetResult<InvoiceWithRelations>> {
    return tryGet<InvoiceWithRelations>(
      `/api/invoices/${encodeURIComponent(id)}?view=relations`,
    );
  },

  getWithVendor(id: string): Promise<GetResult<InvoiceWithVendor>> {
    return tryGet<InvoiceWithVendor>(
      `/api/invoices/${encodeURIComponent(id)}?view=vendor`,
    );
  },

  getForEdit(id: string): Promise<GetResult<InvoiceForEdit>> {
    return tryGet<InvoiceForEdit>(
      `/api/invoices/${encodeURIComponent(id)}?view=edit`,
    );
  },

  list(_companyId: string): Promise<ListResult<Invoice>> {
    return tryList<Invoice>("/api/invoices");
  },

  listWithRelations(_companyId: string): Promise<ListResult<InvoiceListWithRelations>> {
    return tryList<InvoiceListWithRelations>("/api/invoices?withRelations=true");
  },

  listWithRelationsPaginated(
    _companyId: string,
    pagination: PaginationParams,
    filters?: {
      search?: string;
      vendorId?: string | null;
      statusId?: string | null;
      externalSystem?: string | null;
    },
  ): Promise<PaginatedResult<InvoicePaginatedRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
      vendorId: filters?.vendorId,
      statusId: filters?.statusId,
      externalSystem: filters?.externalSystem,
    });
    return tryPaginated<InvoicePaginatedRow>(`/api/invoices/paginated${qs}`);
  },

  listByVendor(vendorId: string): Promise<ListResult<InvoiceByParent>> {
    return tryList<InvoiceByParent>(
      `/api/invoices/by-vendor/${encodeURIComponent(vendorId)}`,
    );
  },

  listByPriceBook(priceBookId: string): Promise<ListResult<InvoiceByParent>> {
    return tryList<InvoiceByParent>(
      `/api/invoices/by-price-book/${encodeURIComponent(priceBookId)}`,
    );
  },

  listForEarlyPay(_companyId: string, statusId?: string | null): Promise<ListResult<EarlyPayRow>> {
    const qs = toQueryString({ statusId });
    return tryList<EarlyPayRow>(`/api/invoices/early-pay${qs}`);
  },

  create(data: InvoiceInsert, _context: ServiceContext): Promise<CreateResult<Invoice>> {
    return tryCreate<Invoice>("/api/invoices", data);
  },

  // currentDiscountInfo is accepted for backward-compat with the prior service
  // signature (used by the still-client-side invoiceItemService until batch 2
  // migrates it). The server fetches its own snapshot and recomputes
  // discount_amt internally — this param is ignored by the route.
  update(
    id: string,
    data: InvoiceUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
    _currentDiscountInfo?: { discount: number | null; discount_amt: number | null },
  ): Promise<UpdateResult<Invoice>> {
    return tryUpdate<Invoice>(`/api/invoices/${encodeURIComponent(id)}`, data);
  },

  delete(
    id: string,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<DeleteResult> {
    return tryDelete(`/api/invoices/${encodeURIComponent(id)}`);
  },

  updateStatus(
    id: string,
    newStatusId: string,
    _previousStatusId: string | null,
    _context: ServiceContext,
    options?: StatusChangeOptions,
  ): Promise<UpdateResult<Invoice>> {
    return tryUpdatePost<Invoice>(
      `/api/invoices/${encodeURIComponent(id)}/status`,
      {
        to_status_id: newStatusId,
        comments: options?.comments ?? null,
        due_to_reassignment: options?.dueToReassignment ?? null,
      },
    );
  },
};
